import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { projectId, type ProjectId } from './Project.js'
import { reportTemplateId, type ReportTemplateId } from './ReportTemplate.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.report_configuration_ */
export type ReportConfigurationId = number & {
  __brand: 'ReportConfigurationId'
}

/** Represents the table public.report_configuration_ */
export default interface ReportConfigurationTable {
  /** Database type: pg_catalog.int4 */
  id: ColumnType<
    ReportConfigurationId,
    ReportConfigurationId | undefined,
    ReportConfigurationId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.int4 */
  projectId: ColumnType<ProjectId, ProjectId, ProjectId>

  /** Database type: pg_catalog.int4 */
  templateId: ColumnType<ReportTemplateId, ReportTemplateId, ReportTemplateId>

  /** Database type: pg_catalog.text */
  name: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.int4[] */
  siteIds: ColumnType<number[], number[] | undefined, number[]>

  /** Database type: pg_catalog.int4 */
  lookbackDays: ColumnType<number, number | undefined, number>

  /** Database type: pg_catalog.jsonb */
  config: ColumnType<unknown, unknown | undefined, unknown>
}

export type ReportConfiguration = Selectable<ReportConfigurationTable>

export type ReportConfigurationInitializer =
  Insertable<ReportConfigurationTable>

export type ReportConfigurationMutator = Updateable<ReportConfigurationTable>

export const reportConfigurationId =
  z.number() as unknown as z.Schema<ReportConfigurationId>

export const reportConfiguration = z.object({
  id: reportConfigurationId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  projectId: projectId,
  templateId: reportTemplateId,
  name: z.string().nullable().optional(),
  siteIds: z.number().array(),
  lookbackDays: z.number(),
  config: z.unknown(),
}) as unknown as z.Schema<ReportConfiguration>

export const reportConfigurationInitializer = z.object({
  id: reportConfigurationId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  projectId: projectId,
  templateId: reportTemplateId,
  name: z.string().optional().nullable(),
  siteIds: z.number().array().optional(),
  lookbackDays: z.number().optional(),
  config: z.unknown().optional(),
}) as unknown as z.Schema<ReportConfigurationInitializer>

export const reportConfigurationMutator = z.object({
  id: reportConfigurationId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  projectId: projectId.optional(),
  templateId: reportTemplateId.optional(),
  name: z.string().optional().nullable(),
  siteIds: z.number().array().optional(),
  lookbackDays: z.number().optional(),
  config: z.unknown().optional(),
}) as unknown as z.Schema<ReportConfigurationMutator>
